import React, { useEffect, useRef, useState } from 'react';
import './CommonStyles.css';
import './ViewSaleModalStyles.css';
import apiService from '../../services/api';
import { useBusinessSettings } from '../../contexts/BusinessSettingsContext';


const ViewSaleModal = ({ sale, onClose, onPackingSlip, onPrintInvoice }) => {
  const { formatCurrency, formatDate, formatDateTime, quantityPrecision } = useBusinessSettings();
  const contentRef = useRef(null);
  const [locations, setLocations] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [products, setProducts] = useState({});
  const [loadingProducts, setLoadingProducts] = useState(true);

  // Fetch locations, users, and products in background
  useEffect(() => {
    let isMounted = true;
    const apiData = sale.apiData || sale;

    // Fetch locations in background
    apiService.getLocations()
      .then(res => {
        if (!isMounted) return;
        const data = res.data || res;
        if (Array.isArray(data)) setLocations(data);
      })
      .catch(() => {})
      .finally(() => { if (isMounted) setLoadingLocations(false); });

    // Fetch users in background
    apiService.getUsers({ per_page: -1 })
      .then(res => {
        if (!isMounted) return;
        const data = res.data || res;
        if (Array.isArray(data)) setUsers(data);
        else if (data.data && Array.isArray(data.data)) setUsers(data.data);
      })
      .catch(() => {})
      .finally(() => { if (isMounted) setLoadingUsers(false); });

    // Fetch products if sell_lines exist
    const sellLines = apiData.sell_lines || [];
    const productIds = [...new Set(sellLines.map(line => line.product_id).filter(id => id))];

    if (productIds.length > 0) {
      const productDetails = {};
      Promise.all(productIds.map(async (productId) => {
        try {
          const productResponse = await apiService.getProduct(productId);
          const productData = productResponse.data || productResponse;
          const product = Array.isArray(productData) ? productData[0] : productData.data || productData;
          productDetails[productId] = product;
        } catch (_) {}
      })).then(() => {
        if (isMounted) {
          setProducts(productDetails);
          setLoadingProducts(false);
        }
      });
    } else {
      setLoadingProducts(false);
    }

    return () => { isMounted = false; };
  }, [sale]);

  // Get location name by ID
  const getLocationName = (locationId) => {
    if (!locationId) return 'N/A';
    const location = locations.find(loc => loc.id === parseInt(locationId));
    return location ? location.name : `Location ID: ${locationId}`;
  };

  // Get username by ID
  const getUsername = (userId) => {
    if (!userId) return 'Admin';
    const user = users.find(u => u.id === parseInt(userId));
    return user ? (user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : user.username || user.name || `User ${userId}`) : `User ${userId}`;
  };

  useEffect(() => {
    // Scroll to top when modal opens
    if (contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  }, [sale]);

  if (!sale) {
    return null;
  }



  // Handle both API data structure and local storage structure
  const apiData = sale.apiData || sale;
  const isApiData = !!sale.apiData;

  const computeTotalPaid = () => {
    if (isApiData && apiData) {
      if (apiData.total_paid !== undefined && apiData.total_paid !== null) {
        return parseFloat(apiData.total_paid) || 0;
      }
      if (Array.isArray(apiData.payment_lines)) {
        return apiData.payment_lines.reduce((sum, p) => {
          const isBounced = p.is_bounced == 1 || p.is_bounced === true || p.is_bounced === '1';
          return sum + (isBounced ? 0 : (parseFloat(p.amount) || 0));
        }, 0);
      }
    }
    const finalTot = parseFloat(sale.finalTotal || sale.totalPayable || sale.total_payable || 0);
    const due = parseFloat(sale.sellDue || sale.payment_due || 0);
    if (sale.sellDue !== undefined && sale.sellDue !== null && sale.sellDue !== '') {
      return Math.max(0, finalTot - due);
    }
    const statusLower = String(sale.paymentStatus || sale.payment_status || '').toLowerCase();
    if (statusLower === 'due') return 0;
    return finalTot;
  };

  const computeTotalRemaining = () => {
    const finalTot = parseFloat(isApiData ? (apiData.final_total || 0) : (sale.finalTotal || sale.totalPayable || sale.total_payable || 0));
    const paid = computeTotalPaid();
    return Math.max(0, finalTot - paid);
  };

  return (
    <div className="modal-overlay">
      <div className="modal-container invoice-modal">
        <div className="modal-header">
          <h2>Sell Details ( Invoice No. : {isApiData ? apiData.invoice_no : sale.invoiceNo} )</h2>
          <button className="modal-close-btn" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="modal-content invoice-content" ref={contentRef}>
          {/* Invoice Header */}
          <div className="invoice-header-section">
            <div className="invoice-info-item">
              <strong>Invoice No. :</strong> #{isApiData ? apiData.invoice_no : sale.invoiceNo}
            </div>
            <div className="invoice-info-item">
              <strong>Date:</strong> {isApiData ? formatDate(apiData.transaction_date) : formatDate(sale.saleDate)}
            </div>
            <div className="invoice-info-item">
              <strong>Status:</strong> {isApiData ? apiData.status : sale.status}
            </div>
            <div className="invoice-info-item">
              <strong>Payment Status:</strong> {isApiData ? apiData.payment_status : sale.paymentStatus}
            </div>
            <div className="invoice-info-item">
              <strong>Location:</strong> {loadingLocations ? 'Loading...' : getLocationName(isApiData ? apiData.location_id : sale.locationId)}
            </div>
          </div>

          {/* Customer and Service Info */}
          <div className="customer-service-section">
            <div className="customer-info-box">
              <div className="info-item">
                <strong>Customer name:</strong> {isApiData ? apiData.contact?.name : sale.customer}
              </div>
              <div className="info-item">
                <strong>Address:</strong>
                <div>{isApiData ? (apiData.contact?.address_line_1 || '--') : (sale.billingAddress || '--')}</div>
              </div>
              <div className="info-item">
                <strong>Mobile:</strong> {isApiData ? (apiData.contact?.mobile || '--') : (sale.contactNumber || '--')}
              </div>
            </div>
            <div className="service-info-box">
              <div className="info-item">
                <strong>Service staff:</strong>
                <div>{isApiData ? (apiData.service_staff || '--') : (sale.serviceStaff || '--')}</div>
              </div>
              <div className="info-item">
                <strong>Shipping:</strong>
                <div>{isApiData ? (apiData.shipping_status || '--') : (sale.shippingStatus || '--')}</div>
              </div>
            </div>
          </div>

          {/* Shipping Details Section */}
          {(isApiData ? (apiData.shipping_address || apiData.shipping_city || apiData.shipping_state) :
            (sale.shippingAddress || sale.shippingCity || sale.shippingState)) && (
              <div className="invoice-section">
                <h3>Shipping Details:</h3>
                <div className="shipping-info-box">
                  <div className="info-item">
                    <strong>Shipping Address:</strong>
                    <div>
                      {isApiData ? (apiData.shipping_address || '--') : (sale.shippingAddress || '--')}
                    </div>
                  </div>
                  <div className="info-item">
                    <strong>City:</strong>
                    <div>{isApiData ? (apiData.shipping_city || '--') : (sale.shippingCity || '--')}</div>
                  </div>
                  <div className="info-item">
                    <strong>State:</strong>
                    <div>{isApiData ? (apiData.shipping_state || '--') : (sale.shippingState || '--')}</div>
                  </div>
                  <div className="info-item">
                    <strong>Country:</strong>
                    <div>{isApiData ? (apiData.shipping_country || '--') : (sale.shippingCountry || '--')}</div>
                  </div>
                  <div className="info-item">
                    <strong>ZIP Code:</strong>
                    <div>{isApiData ? (apiData.shipping_zip_code || '--') : (sale.shippingZipCode || '--')}</div>
                  </div>
                  <div className="info-item">
                    <strong>Shipping Status:</strong>
                    <div>{isApiData ? (apiData.shipping_status || '--') : (sale.shippingStatus || '--')}</div>
                  </div>
                </div>
              </div>
            )}

          {/* Products Section */}
          <div className="invoice-section">
            <h3>Products:</h3>
            <div className="invoice-table-wrapper">
              <table className="invoice-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Product</th>
                    <th>UPC / SKU</th>
                    <th>Quantity</th>
                    <th>Unit Price</th>
                    <th>Discount</th>
                    <th>Tax</th>
                    <th>Price inc. tax</th>
                    <th>Subtotal</th>
                  </tr>
                </thead>
                <tbody>
                  {isApiData && apiData.sell_lines ? (
                    apiData.sell_lines.map((line, index) => {
                      const product = products[line.product_id];
                      return (
                        <tr key={line.id}>
                          <td>{index + 1}</td>
                          <td>{product?.name || line.product?.name || `Product #${line.product_id}`}</td>
                          <td>{product?.sku || product?.barcode || line.product?.sku || line.product?.barcode || '--'}</td>
                          <td>{parseFloat(line.quantity).toFixed(quantityPrecision)} {line.unit || 'Single'}</td>
                          <td>{formatCurrency(line.unit_price)}</td>
                          <td>{formatCurrency(line.line_discount_amount || 0)}</td>
                          <td>{formatCurrency(line.item_tax)} {line.tax_name ? `( ${line.tax_name} )` : ''}</td>
                          <td>{formatCurrency(line.unit_price_inc_tax)}</td>
                          <td>{formatCurrency((parseFloat(line.unit_price_inc_tax) * parseFloat(line.quantity)).toFixed(2))}</td>
                        </tr>
                      );
                    })
                  ) : sale.products && sale.products.length > 0 ? (
                    sale.products.map((product, index) => (
                      <tr key={product.id}>
                        <td>{index + 1}</td>
                        <td>{product.name}</td>
                        <td>{product.sku || '--'}</td>
                        <td>{product.quantity} Single</td>
                        <td>{formatCurrency(product.unitPrice)}</td>
                        <td>{formatCurrency(product.discount || 0)}</td>
                        <td>{formatCurrency(0)} {product.tax ? `( ${product.tax} )` : ''}</td>
                        <td>{formatCurrency(product.priceIncTax)}</td>
                        <td>{formatCurrency(product.subtotal)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="9" style={{ textAlign: 'center' }}>No products found</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Payment Info and Totals Combined Section */}
          <div className="payment-totals-wrapper">
            {/* Payment Info Section */}
            {isApiData && apiData.payment_lines && apiData.payment_lines.length > 0 && (
              <div className="invoice-section payment-info-section">
                <h3>Payment info:</h3>
                <div className="invoice-table-wrapper">
                  <table className="invoice-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Date</th>
                        <th>Reference No</th>
                        <th>Amount</th>
                        <th>Check Return Charges</th>
                        <th>Payment mode</th>
                        <th>Payment note</th>
                      </tr>
                    </thead>
                    <tbody>
                      {apiData.payment_lines.map((payment, index) => (
                        <tr key={payment.id}>
                          <td>{index + 1}</td>
                          <td>{formatDate(payment.paid_on)}</td>
                          <td>{payment.payment_ref_no || '--'}</td>
                          <td>{formatCurrency(payment.amount)}</td>
                          <td>{formatCurrency(payment.check_return_charges || 0)}</td>
                          <td>{payment.method}</td>
                          <td>{payment.note || '--'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Totals Section */}
            <div className="totals-section">
              <div className="totals-grid">
                <div className="total-row">
                  <span className="total-label">Total:</span>
                  <span className="total-value">{isApiData ? formatCurrency(apiData.total_before_tax) : formatCurrency(sale.productAmount || sale.totalPayable)}</span>
                </div>
                {isApiData && apiData.check_return_charges && parseFloat(apiData.check_return_charges) > 0 && (
                  <div className="total-row">
                    <span className="total-label">Check Return Charges:</span>
                    <span className="total-value">{formatCurrency(apiData.check_return_charges)}</span>
                  </div>
                )}
                <div className="total-row">
                  <span className="total-label">Discount:</span>
                  <span className="total-value">(-) {isApiData ? (apiData.discount_type === 'percentage' ? `${apiData.discount_amount}%` : formatCurrency(apiData.discount_amount)) : formatCurrency(sale.discountAmount || 0)}</span>
                </div>
                <div className="total-row">
                  <span className="total-label">Order Tax:</span>
                  <span className="total-value">(+) {isApiData ? formatCurrency(apiData.tax_amount || 0) : formatCurrency(sale.orderTaxAmount || 0)}</span>
                </div>
                {isApiData && apiData.sell_lines && (
                  <div className="total-row">
                    <span className="total-label">Line taxes:</span>
                    <span className="total-value">
                      {apiData.sell_lines.map(line => line.tax_name).filter((v, i, a) => a.indexOf(v) === i).join(', ') || '--'} -
                      {formatCurrency(apiData.sell_lines.reduce((sum, line) => sum + parseFloat(line.item_tax || 0), 0))}
                    </span>
                  </div>
                )}
                <div className="total-row">
                  <span className="total-label">Shipping:</span>
                  <span className="total-value">(+) {isApiData ? formatCurrency(apiData.shipping_charges || 0) : formatCurrency(sale.shippingCharges || 0)}</span>
                </div>
                <div className="total-row">
                  <span className="total-label">Round Off:</span>
                  <span className="total-value">{isApiData ? formatCurrency(apiData.round_off || 0) : formatCurrency(sale.roundOff || 0)}</span>
                </div>
                <div className="total-row total-payable-row">
                  <span className="total-label">Total Payable:</span>
                  <span className="total-value">{isApiData ? formatCurrency(apiData.final_total) : formatCurrency(sale.totalPayable)}</span>
                </div>
                <div className="total-row">
                  <span className="total-label">Total paid:</span>
                  <span className="total-value">{formatCurrency(computeTotalPaid())}</span>
                </div>
                <div className="total-row">
                  <span className="total-label">Total remaining:</span>
                  <span className="total-value">
                    {formatCurrency(computeTotalRemaining())}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Invoice URL Section */}
          {(isApiData && apiData.invoice_url) || sale.invoiceUrl ? (
            <div className="invoice-url-section">
              <h3>Invoice URL:</h3>
              <div className="invoice-url-container">
                <input
                  type="text"
                  value={isApiData ? apiData.invoice_url : sale.invoiceUrl}
                  readOnly
                  className="invoice-url-input"
                />
                <button
                  className="url-btn copy-btn"
                  onClick={() => {
                    navigator.clipboard.writeText(isApiData ? apiData.invoice_url : sale.invoiceUrl);
                    alert('Invoice URL copied to clipboard!');
                  }}
                >
                  📋 Copy
                </button>
                <button
                  className="url-btn open-btn"
                  onClick={() => window.open(isApiData ? apiData.invoice_url : sale.invoiceUrl, '_blank')}
                >
                  🔗 Open
                </button>
              </div>
            </div>
          ) : null}

          {/* Notes Section */}
          <div className="notes-section">
            <div className="note-item">
              <h4>Sell note:</h4>
              <p>{isApiData ? (apiData.additional_notes || '--') : (sale.sellNote || '--')}</p>
            </div>
            <div className="note-item">
              <h4>Staff note:</h4>
              <p>{isApiData ? (apiData.staff_note || '--') : '--'}</p>
            </div>
          </div>

          {/* Activities Section */}
          <div className="invoice-section">
            <h3>Activities:</h3>
            <div className="invoice-table-wrapper">
              <table className="invoice-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Action</th>
                    <th>By</th>
                    <th>Note</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{isApiData ? formatDateTime(apiData.created_at) : formatDateTime(sale.saleDate)}</td>
                    <td>Added</td>
                    <td>{isApiData ? getUsername(apiData.created_by) : (sale.createdBy || 'Admin')}</td>
                    <td>
                      <div>Status: {isApiData ? apiData.status : sale.status}</div>
                      <div>Total: {isApiData ? formatCurrency(apiData.final_total) : formatCurrency(sale.totalPayable)}</div>
                      <div>Payment Status: {isApiData ? apiData.payment_status : sale.paymentStatus}</div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button className="modal-btn packing-slip-btn" onClick={() => onPackingSlip(sale)}>
            📋 Packing Slip
          </button>
          <button
            className="modal-btn print-invoice-btn"
            onClick={() => {
              const invoiceUrl = isApiData ? apiData.invoice_url : sale.invoiceUrl;
              if (invoiceUrl) {
                window.open(invoiceUrl, '_blank');
              } else {
                alert('Invoice URL not available for this sale.');
              }
            }}
          >
            🖨️ Print Invoice
          </button>
        </div>
      </div>
    </div>
  );
};

export default ViewSaleModal;
